import React, { useEffect } from 'react';
import { Form } from 'antd';

import { setStaticFormValuesFromFilter } from '../../common/filterHelpers';
import { SearchFormProps } from '../../common/searchFormField.types';
import { PriorityEnum, RemotePreferenceEnum } from '../../common/types';
import { useGetAllClients } from '../../common/useGetAllClients';
import { useGetAllUsers } from '../../common/useGetAllUsers';
import { useGetRecommendersDictionary } from '../../common/useGetRecommendersDictionary';
import { FormCheckbox } from '../../components/forms/FormCheckbox';
import { FormCheckboxGroup } from '../../components/forms/FormCheckboxGroup';
import { FormDatePicker } from '../../components/forms/FormDatePicker';
import { FormDoubleInput } from '../../components/forms/FormDoubleInput';
import { FormInput } from '../../components/forms/FormInput';
import { FormProjectSelectWithSearch } from '../../components/forms/FormProjectSelectWithSearch';
import { FormSelect } from '../../components/forms/FormSelect';
import { StyledColumn } from '../../components/StyledColumn';

export const remotePreferenceOptions = Object.values(RemotePreferenceEnum).map((preference) => ({
  value: preference,
  content: preference,
}));

export const RecruitmentsSearchForm: React.FC<SearchFormProps> = ({ form, filter }) => {
  useEffect(() => setStaticFormValuesFromFilter(form, filter), [filter, form]);

  const { data: clientsData, isLoading: clientsLoading } = useGetAllClients();
  const clientOptions =
    !clientsLoading && clientsData
      ? clientsData.results.map((client) => ({
          value: client.name,
          content: client.name,
        }))
      : [];

  const { data: usersData, isLoading: usersLoading } = useGetAllUsers();
  const userOptions =
    !usersLoading && usersData
      ? usersData.results.map((user) => ({
          value: user.id,
          content: `${user.firstName} ${user.lastName}`,
        }))
      : [];

  const { data: recommenders, isLoading: recommendersLoading } = useGetRecommendersDictionary();
  const recommenderOptions =
    recommenders && !recommendersLoading
      ? recommenders.map((recommender) => ({ value: recommender.value, content: recommender.value }))
      : [];

  const priorityOptions = Object.values(PriorityEnum);

  return (
    <Form form={form} style={{ flexGrow: '1' }} layout="vertical" colon={false}>
      <StyledColumn gap="0.5rem">
        <FormDoubleInput
          leftInput={<FormInput name="candidate.firstName" label="Candidate first name" />}
          rightInput={<FormInput name="candidate.lastName" label="Candidate last name" />}
        />

        <FormDoubleInput
          leftInput={<FormDatePicker name={['createdDate', 'start']} label="Created from" />}
          rightInput={<FormDatePicker name={['createdDate', 'end']} label="Created to" />}
          withDivider
        />

        <FormDoubleInput
          leftInput={<FormDatePicker name={['lastModifiedDate', 'start']} label="Last modified from" />}
          rightInput={<FormDatePicker name={['lastModifiedDate', 'end']} label="Last modified to" />}
          withDivider
        />
        <FormSelect name="candidate.recommendedBy" label="Recommended by" options={recommenderOptions} />

        <FormProjectSelectWithSearch />

        <FormSelect name="project.client.name" label="Project client name" options={clientOptions} />

        <FormCheckbox label="Active" name="project.active" />

        <FormCheckbox label="Action required" name="status.actionRequired" />

        <FormCheckbox label="Perfect match" name="perfectMatch" />

        <FormCheckboxGroup options={priorityOptions} label="Priority" name="project.priority" />

        <FormSelect name="project.backoffices.id" label="Project backoffice" options={userOptions} />

        <FormSelect name="project.coordinators.id" label="Project coordinator" options={userOptions} />

        <FormSelect name="project.interviewers.id" label="Project interviewer" options={userOptions} />

        <FormSelect name="project.recruiters.id" label="Project recruiter" options={userOptions} />

        <FormSelect name="responsible.id" label="Responsible" options={userOptions} />
      </StyledColumn>
    </Form>
  );
};
